import {createContext, useContext, useEffect, useState, type FC} from 'react'
import {useMutation} from '@tanstack/react-query'
import {getOperators} from '@/queries/operator'
import {OperatorSettings, operatorSettings} from '@/constants/operators'
import { configureEcho, useEchoPublic } from '@laravel/echo-react';


configureEcho({
  broadcaster: "reverb",
  wsPort: 80,
  wssPort:443,
  forceTLS: false,
  authorizer: (channel:any) => {
    const token = JSON.parse(localStorage.getItem('user-store') || '').token
    return {
      authorize: (socketId:any, callback:any) => {
        fetch(`${import.meta.env.VITE_APP_API_URL.replace('api/','broadcasting/auth')}`, {
          method: "POST",
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`, // Or include cookies for Sanctum
          },
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name
          })
        })
        .then(res => res.json())
        .then(data => callback(false, data))
        .catch(err => callback(true, err));
      }
    };
  }
});

export type OperatorsName = 'irancell' | 'rightel' | 'shatel' | 'mci' | 'aptel' | 'td-lte'

interface IOperator {
  id: number,
  name: string
  title: string
  status: boolean
  setting: {
    credit_cell_internet: boolean
    credit_td_lte_internet: boolean
    credit_cell_direct_charge: boolean
    credit_cell_internet_direct_charge: boolean
    permanent_cell_internet: boolean
    permanent_td_lte_internet: boolean
    permanent_cell_direct_charge: boolean
    permanent_cell_internet_direct_charge: boolean
    bill: boolean
  }
}

interface IStatus {
  title: string
  status: boolean
  name: OperatorsName
  setting: {
    title: string
    status: boolean
  }[]
}

interface IOperatorContext {
  operators?: IOperator[]
  refetch: () => void
  isLoading: boolean
  status: IStatus[],
  checkOperator: (operator: OperatorsName) => boolean
  checkStatus: (operator: OperatorsName, setting: keyof typeof OperatorSettings) => boolean
}

const defaultContext: IOperatorContext = {
  operators: [],
  refetch: () => {},
  isLoading: false,
  status: [],
  checkOperator: () => false,
  checkStatus() {
    return false
  },
}

const OperatorContext = createContext<IOperatorContext>(defaultContext)

export const useOperator = () => {
  return useContext(OperatorContext)
}

interface OperatorProviderProps {
  children: React.ReactNode
}

const OperatorProvider: FC<OperatorProviderProps> = ({children}) => {
  const [operators, setOperators] = useState<IOperator[]>([])
  const [status, setStatus] = useState<IStatus[]>([])

  useEchoPublic("operators", '.OperatorUpdated', function(e: IOperator) {
    setOperators(s => s.map(o => {
      if (o.id == e.id) {
        return e
      }
      return o
    }))
  })

  useEffect(()=> {
    const os = operators
    setStatus(
      (Object.keys(operatorSettings) as OperatorsName[]).map((k) => {
        const operator: IStatus = {
          name: k,
          title: os.find((o) => o.name.toLowerCase() === k || o.name === k)?.title ?? '',
          status: os.find((o) => o.name.toLowerCase() === k || o.name === k)?.status ?? false,
          setting: [],
        }

        operator.setting = (
          Object.keys(operatorSettings[k]) as (keyof typeof OperatorSettings)[]
        ).map((s,) => ({
          title: operatorSettings[k][s] ?? '',
          status: Boolean(os?.find((o) => o.name.toLowerCase() === k || o.name === k)?.setting[s]),
        }))

        return operator
      }).filter(o => o.name !== 'td-lte')
    )
  }, [operators])

  const {mutate, isPending} = useMutation({
    mutationFn: () => getOperators(),
    mutationKey: ['getOperatorStatus'],
    onSuccess(data: any) {
      if (data.data) {
        if (data.data.data) {
          const os = data.data.data as IOperator[]
          setOperators(os)

          setStatus(
            (Object.keys(operatorSettings) as OperatorsName[]).map((k,) => {
              const operator: IStatus = {
                name: k,
                title: os.find((o) => o.name.toLowerCase() === k || o.name === k)?.title ?? '',
                status: os.find((o) => o.name.toLowerCase() === k || o.name === k)?.status ?? false,
                setting: [],
              }

              operator.setting = (
                Object.keys(operatorSettings[k]) as (keyof typeof OperatorSettings)[]
              ).map((s,) => ({
                title: operatorSettings[k][s] ?? '',
                status: Boolean(os?.find((o) => o.name.toLowerCase() === k || o.name === k)?.setting[s]),
              }))

              return operator
            }).filter(o => o.name !== 'td-lte')
          )
        }
      }
    },
  })

  useEffect(() => {
    mutate()
  }, [])

  const checkOperator = (operator: OperatorsName) => {
    let s = status.find(o => o.name.toLowerCase() === operator.toLowerCase())
    if (operator === 'td-lte') {
      s = status.find(o => o.name.toLowerCase() === 'irancell')
    }
    return Boolean(s?.status)
  }

  const checkStatus = (operator: OperatorsName, setting: keyof typeof OperatorSettings) => {
    
    let o = operators.find(o => o.name.toLowerCase() === operator.toLowerCase())
    if (operator === 'td-lte') {
      o = operators.find(s => s.name.toLocaleLowerCase() === 'irancell')
    }
    if (o === undefined) {
      return false
    }
    const settings = o.setting[setting.replace('_amazing', '') as keyof typeof OperatorSettings]
    if (settings == undefined) {
      return false
    }
    return Boolean(settings)
  }

  return (
    <>
      <OperatorContext.Provider value={{operators, refetch: mutate, isLoading: isPending, status, checkOperator,checkStatus}}>
        {children}
      </OperatorContext.Provider>
    </>
  )
}

export default OperatorProvider
